import React, { useState, useEffect } from "react";
import {
  TextField,
  Grid,
  Container,
  Typography,
  Paper,
  Snackbar,
  Alert,
  Button,
  Box,
  Stack,
} from "@mui/material";

const OrganizationInfo = () => {
  const [organizationName, setOrganizationName] = useState("");
  const [address, setAddress] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [isEditable, setIsEditable] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "" });
  const [originalData, setOriginalData] = useState({});

  const token = localStorage.getItem("access_token");

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/profile`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) throw new Error(`Failed to fetch: ${response.statusText}`);

        const data = await response.json();

        const orgData = {
          organizationName: data.organization.name || "",
          address: data.organization.address || "",
          contactEmail: data.organization.contact_email || "",
          phone: data.organization.phone || "",
        };

        setOriginalData(orgData);
        setOrganizationName(orgData.organizationName);
        setAddress(orgData.address);
        setContactEmail(orgData.contactEmail);
        setPhone(orgData.phone);
      } catch (error) {
        setSnackbar({ open: true, message: `Error: ${error.message}`, severity: "error" });
      }
    };

    fetchProfile();
  }, [token]);

  const handleEditClick = () => {
    setOriginalData({ organizationName, address, contactEmail, phone });
    setIsEditable(true);
  };

  const handleCancel = () => {
    setOrganizationName(originalData.organizationName);
    setAddress(originalData.address);
    setContactEmail(originalData.contactEmail);
    setPhone(originalData.phone);
    setIsEditable(false);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_NEW_HOST_URL}/athrv/api/v1/organizations`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            name: organizationName,
            address,
            contact_email: contactEmail,
            phone,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSnackbar({ open: true, message: "Organization info updated successfully!", severity: "success" });
        setIsEditable(false);
        setOriginalData({ organizationName, address, contactEmail, phone });
      } else {
        setSnackbar({ open: true, message: data.message || "Failed to update organization info", severity: "error" });
      }
    } catch (error) {
      setSnackbar({ open: true, message: `Error: ${error.message}`, severity: "error" });
    } finally {
      setIsSaving(false);
    }
  };
  const formatAddress = (address) => {
    const chunkSize = 25;
    const formatted = [];
    for (let i = 0; i < address.length; i += chunkSize) {
      formatted.push(address.substring(i, i + chunkSize));
    }
    return formatted;
  };

  const renderField = (label, value, setter, editable) => (
    <Box display="flex" flexDirection="column" mb={2}>
      <Box display="flex" alignItems="center">
        <Typography variant="subtitle1" sx={{ width: "150px", fontWeight: "bold" }}>
          {label}
        </Typography>
        {isEditable && editable ? (
          <TextField value={value} onChange={(e) => setter(e.target.value)} variant="outlined" size="small" fullWidth />
        ) : (
          <>
            {label === "Address:" ? (
              <Box>
                {formatAddress(value).map((chunk, index) => (
                  <Typography key={index} variant="body1" sx={{ display: "block" }}>
                    {chunk}
                  </Typography>
                ))}
              </Box>
            ) : (
              <Typography variant="body1">{value}</Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );

  return (
    <Container maxWidth="md">
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Paper elevation={3} sx={{ padding: 3, marginTop: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ marginTop: "10px" }}>
          Organization Information
        </Typography>
        <Grid container spacing={1} sx={{ marginTop: "10px" }}>
          <Grid item xs={12} sm={6}>{renderField("Organization Name:", organizationName, setOrganizationName, true)}</Grid>
          <Grid item xs={12} sm={6}>{renderField("Phone:", phone, setPhone, true)}</Grid>
          <Grid item xs={12} sm={6}>{renderField("Address:", address, setAddress, true)}</Grid>
          <Grid item xs={12} sm={6}>{renderField("Contact Email:", contactEmail, setContactEmail, false)}</Grid>
        </Grid>

        <Stack direction="row" spacing={2} sx={{ marginTop: 2 }}>
          {!isEditable ? (
            <Button variant="contained" className="athrv-dash-primary-button" onClick={handleEditClick}>
              Edit
            </Button>
          ) : (
            <>
              <Button variant="contained" color="success" className="athrv-dash-primary-button" onClick={handleSave} disabled={isSaving}>
                {isSaving ? "Saving..." : "Save"}
              </Button>
              <Button variant="contained" color="error" onClick={handleCancel} disabled={isSaving}>
                Cancel
              </Button>
            </>
          )}
        </Stack>
      </Paper>
    </Container>
  );
};

export default OrganizationInfo;